import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Container } from 'reactstrap';
import MetaTags from 'react-meta-tags';
import { useAuth } from '../../store/auth';
import { privacyContent } from '../../data';
import { privacyContentArabic } from '../../data/indexArabic';

const PagePrivacy = () => {
    const { isArabic } = useAuth();
    const data = isArabic ? privacyContent : privacyContentArabic;
    const [activeSection, setActiveSection] = useState('');

    // --- Data Structuring ---
    // To make the data object iterable for navigation without changing the original JSON,
    // we define the order of sections explicitly.
    const sectionOrder = [
        'introduction', 'collectionInfo', 'useInfo', 'userPersonalInfo',
        'accessingInfo', 'thirdPartyLinks', 'cookies', 'noSpam',
        'protectingInfo', 'contactUs'
    ];
    
    // Create an array of refs to reference each section's DOM element
    const sectionRefs = useRef(sectionOrder.reduce((acc, value) => {
        acc[value] = React.createRef();
        return acc;
    }, {}));


    // --- Effects for Interactivity and Navbar ---
    useEffect(() => {
        // Effect 1: Safe handling of the global sticky navbar
        const handleGlobalNavScroll = () => {
            const topnav = document.getElementById("topnav");
            if (topnav) {
                topnav.classList.toggle("nav-sticky", window.scrollY > 80);
            }
        };
        window.addEventListener('scroll', handleGlobalNavScroll);

        // Effect 2: Scrollspy for the local sidebar navigation
        const handleScrollSpy = () => {
            const currentPosition = window.scrollY + 150; // Offset for better accuracy
            let currentSection = '';

            sectionOrder.forEach(key => {
                const section = sectionRefs.current[key].current;
                if (section && section.offsetTop <= currentPosition) {
                    currentSection = key;
                }
            });

            if (currentSection !== activeSection) {
                setActiveSection(currentSection);
            }
        };
        window.addEventListener('scroll', handleScrollSpy);

        // Cleanup function to remove all event listeners
        return () => {
            window.removeEventListener('scroll', handleGlobalNavScroll);
            window.removeEventListener('scroll', handleScrollSpy);
        };
    }, [activeSection]); // Rerun scrollspy logic when activeSection changes

    return (
        <React.Fragment>
            <MetaTags>
                <title>{"Privacy Policy | Subul Contracting"}</title>
            </MetaTags>

            {/* Section 1: Page Header */}
            <section className="bg-half-170 bg-light d-table w-100">
                <Container>
                    <Row className="mt-5 justify-content-center">
                        <Col lg={12} className="text-center">
                            <div className="pages-heading">
                                <h1 className="title">{data?.title}</h1>
                            </div>
                        </Col>
                    </Row>
                    <div className="position-breadcrumb">
                        <nav aria-label="breadcrumb" className="d-inline-block">
                            <ul className="breadcrumb rounded shadow mb-0 px-4 py-2">
                                {data?.breadcrumb?.map((item, index) => (
                                    <li className={item?.className} key={index}>
                                        {item?.link ? <Link to={item.link}>{item.name}</Link> : item?.name}
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </Container>
            </section>

            <div className="position-relative">
                <div className="shape overflow-hidden text-white">
                    <svg viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor"></path>
                    </svg>
                </div>
            </div>

            {/* Section 2: Redesigned Privacy Content with Sticky Navigation */}
            <section className="section">
                <Container>
                    <Row>
                        {/* Column 1: Sticky Navigation Sidebar */}
                        <Col lg={4} md={5} className="d-none d-md-block">
                            <div className="position-sticky" style={{ top: '100px' }}>
                                <div className="privacy-nav">
                                    <ul className="nav nav-pills flex-column">
                                        {sectionOrder.map(key => data[key] && (
                                            <li className="nav-item" key={key}>
                                                <a 
                                                    href={`#${key}`} 
                                                    className={`nav-link ${activeSection === key ? 'active' : ''}`}
                                                >
                                                    {data[key].title}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </Col>

                        {/* Column 2: Main Policy Content */}
                        <Col lg={8} md={7}>
                            <div className="privacy-content">
                                {sectionOrder.map(key => data[key] && (
                                    <div 
                                        key={key} 
                                        id={key} 
                                        ref={sectionRefs.current[key]} 
                                        className="mb-5"
                                    >
                                        <h4 className="card-title mb-3">{data[key].title}</h4>
                                        <p className="text-muted">{data[key].content}</p>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
};

export default PagePrivacy;